<template>
  <div class="tariff-box">
    <form>
      <div class="column-left">
        <div class="tariff--volumes">
          <VolumesAboOption
            ref="volumesAboOptionComp"
            :availableAboOptions="availableAboOptions"
            :targetTariff="targetTariff"
            :volumePromotions="volumePromotions"
            :showPriceStarts="showPriceStarts"
            :process="process">
            <template v-for="slot in possibleChildSlotsFor('AboOptionWrapper')" v-slot:[`${slot.child}`]>
              <slot :name="`${slot.parent}`"></slot>
            </template>
            <div class="volumes-abo-option">
              <fieldset class="form-group">^
                <div role="radiogroup" tabindex="-1" class="btn-group-toggle btn-group bv-no-focus-ring"></div>
              </fieldset>
            </div>
          </VolumesAboOption>
        </div>
        <div class="tariff--boxes">
          <template v-if="infoBoxesForOption && infoBoxesForOption.length">
            <div v-for="(infoBoxForOption, index) in infoBoxesForOption" :key="index + 'option'">
              <InfoBox v-bind="infoBoxForOption" class="card"></InfoBox>
            </div>
          </template>
          <template v-if="infoBoxes && infoBoxes.length">
            <div v-for="(infoBox, index) in infoBoxes" :key="index + 'info'">
              <InfoBox v-bind="infoBox" class="card"></InfoBox>
            </div>
          </template>
          <template v-if="voucherBoxes && voucherBoxes.length">
            <div v-for="(voucherBox, index) in voucherBoxes" :key="index + 'voucher'">
              <VoucherBox v-bind="voucherBox" class="card" :process="process"></VoucherBox>
            </div>
          </template>
        </div>
        <div class="tariff--mnp card">
          <TariffSelect v-if="showTariffSelect" :quickSelectionBrand="quickSelectionBrand" :disableProviderSelect="disableProviderSelect" :process="process">
            <template v-for="slot in possibleChildSlotsFor('TariffSelect')" v-slot:[`${slot.child}`]>
              <slot :name="`${slot.parent}`"></slot>
            </template>
          </TariffSelect>
        </div>
      </div>
      <div class="column-right">
        <div class="tariff--tariff card" :class="[hasVisiblePricePromotion? 'promotion ' + getSlotText('pricePromoColor') : getSlotText('priceColor')]">
          <div class="tariff--name">{{ selectedAboOption.lblName }}</div>
          <span class="sr-only" :aria-live="ariaLivePriceText" aria-atomic="true" v-html="ariaPriceText"></span>
          <div class="tariff--price" :class="{'strikePrice': hasStrikePrice }">
            <div class="price--group">
              <div class="price--prefix" v-if="hasStrikePrice || showPriceStarts">
                <div class="price--strikethrough" aria-hidden="true" v-if="hasStrikePrice">{{priceWithoutEuro(fakeNormalPriceForBirthdayPromo)}}</div>
                <div class="price--starts" aria-hidden="true" v-if="showPriceStarts">ab</div>
              </div>
              <div class="price--amount" aria-hidden="true">{{priceWithoutEuro(promoPrice.promoAmount)}}</div>
              <div class="price--postfix">
                <span aria-hidden="true"><slot name="txtPriceCurrency"></slot></span>
                <button type="button" aria-live="off" aria-haspopup="dialog" :aria-label="getSlotText('txtInfoIconAlt')" class="icon-only price--info" @click="trackAndShowInfoModal()" style="cursor: pointer;">
                  <img src="files/flash/images/svg-icons/info-icon-white.svg" alt="">
                </button>
              </div>
            </div>
            <div class="price--limited" v-if="promoPrice.duration > 0" >
              <div v-for="(stairway, index) in additionalPriceStairways" :key="index" :ref="'limitedPriceText' + index" aria-hidden="true">
                <dyn-slot :slotData="slotDataForStairway(stairway)"><slot name="dynStairway"></slot></dyn-slot>
              </div>
            </div>
          </div>
          <SimonModal :is-visible="showInfoModal" @hide="showInfoModal = false" size="large"
                      :area-label-modal="getSlotText('lblTermsModal', 'Rechtliche Hinweise')"
          >
            <slot name="txtLegalTerms"></slot>
          </SimonModal>
          <div class="bottom--cta">
            <a class="button filled fluid yellow" :href="ctaLink" data-prid="button-checkout" :aria-label="getSlotText('lblAriaLabelCTA')"><slot name="lblOrderButton"></slot></a>
          </div>
          <div class="bottom--highlights" v-if="slotForProductHighlights" v-html="slotForProductHighlights"></div>
          <div class="bottom--more-info" v-if="slotForModalProductHighlights">
            <a class="button text" href="javascript:;" @click="showMoreInfoModal=true" style="cursor: pointer;"><slot name="lblMoreInfoButton"></slot></a>
            <SimonModal :is-visible="showMoreInfoModal" id="modal" @hide="showMoreInfoModal = false"
                        :area-label-modal="getSlotText('areaLabelMoreInfo', 'Mehr Infos')"
            >
              <div v-html="slotForModalProductHighlights"></div>
            </SimonModal>
          </div>
        </div>
        <div class="tariff--bottom">
          <div class="bottom--pib">
            <a class="button text" target="_blank" href="/downloads/pib" rel="noopener"><slot name="lblPibLink"></slot></a>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import VolumesAboOption from '@/components/checkout/tariff/generic/VolumesAboOption.vue'
import VoucherBox from '@/components/checkout/tariff/generic/VoucherBox.vue'
import InfoBox from '@/components/checkout/tariff/generic/InfoBox.vue'
import SimonModal from '@/components/modal/SimonModal.vue'
import TariffSelect from '@/components/checkout/tariff/generic/TariffSelect.vue'
import price from '@/mixins/formatters/price'
import { mapGetters } from 'vuex'
import trackingHandler from '@/mixins/trackingHandler'
import useFeature from '@/mixins/useFeature'

export default {
  name: 'TariffBox',
  data () {
    return {
      showInfoModal: false,
      showMoreInfoModal: false,
      ariaPriceText: '',
      ariaLivePriceText: 'off'
    }
  },
  mixins: [
    price,
    trackingHandler,
    useFeature('birthdayPromo')
  ],
  components: {
    SimonModal,
    VoucherBox,
    InfoBox,
    VolumesAboOption,
    TariffSelect
  },
  mounted () {
    this.getAriaPriceText()
  },
  computed: {
    ...mapGetters('promotion', ['presalesInclusivePromotion']),
    mnpProvider: function () {
      return this.$store.getters['checkoutv2/' + this.process + '/_cart/mnpProvider']
    },
    selectedAboOptionId: function () {
      return this.$store.getters['checkoutv2/' + this.process + '/_cart/selectedTariffOptionIds']?.length > 0 ? this.$store.getters['checkoutv2/' + this.process + '/_cart/selectedTariffOptionIds'][0] : []
    },
    selectedAboOption: function () {
      const aboOption = this.availableAboOptions.find(option => option.optionId === this.selectedAboOptionId)
      return aboOption || this.availableAboOptions.find(option => option.preselectOption === '1')
    },
    availableAboOptions: function () {
      try {
        return this.getSlotText('availableAboOptionsJSON') !== ''
          ? JSON.parse(this.getSlotText('availableAboOptionsJSON'))
          : []
      } catch (e) {
        console.error('Error parsing JSON data:', e)
        return []
      }
    },
    infoBoxesForOption: function () {
      return this.selectedAboOption?.infoBoxes || []
    },
    infoBoxes: function () {
      try {
        return this.getSlotText('infoBoxesJSON') !== ''
          ? JSON.parse(this.getSlotText('infoBoxesJSON'))
          : []
      } catch (e) {
        console.error('Error parsing JSON data:', e)
        return []
      }
    },
    voucherBoxes: function () {
      try {
        return this.getSlotText('voucherBoxesJSON') !== ''
          ? JSON.parse(this.getSlotText('voucherBoxesJSON'))
          : []
      } catch (e) {
        console.error('Error parsing JSON data:', e)
        return []
      }
    },
    slotForProductHighlights () {
      return this.processHTML(this.selectedAboOption?.overwriteTariffDetails === '1'
        ? this.selectedAboOption.txtProductHighlights
        : this.getSlotText('txtProductHighlights'))
    },
    slotForModalProductHighlights () {
      return this.processHTML(this.selectedAboOption?.overwriteTariffDetails === '1'
        ? this.selectedAboOption.infoModalHighlights
        : this.getSlotText('txtMoreInfoModal'))
    },
    filteredPromotions: function () {
      return this.targetPromotions.filter(promo => {
        // promotions that need e.g. voucher code are not displayed in tariff
        if (promo.requiredParameters !== null) return false

        // Tariff Filter -> only promotions that accept the current targetTariff
        if (this.targetTariff && this.targetTariff.tariffId !== null) {
          const tariffIds = promo.criteria?.tariffIds || []
          if (tariffIds.length > 0 && tariffIds.includes(this.targetTariff.tariffId) === false) return false
        }

        // Provider Filter -> only promotions that dont need a certain provider
        const criteriaMnpProvider = promo.criteria?.mnpProvider || []
        return criteriaMnpProvider.length === 0
      })
    },
    creditPromotions () { // only price promotions (permanent and temporary)
      return this.filteredPromotions.filter(promo => promo.incentives?.find(incentive => incentive.type === 'credit'))
    },
    volumePromotions () { // only permanent volume promotions
      return this.filteredPromotions.filter(promo => promo.incentives?.find(incentive => incentive.type === 'volume' && (promo.duration === null)))
    },
    hasPricePromotion () {
      return this.creditPromotions.length > 0
    },
    hasVisiblePricePromotion () {
      // gilt für 30GB Promo und 130GB Option
      if (this.birthdayPromo && ['3036138', '3035138'].includes(this.selectedAboOptionId)) {
        return true
      }
      // kwk promotions enforce no promo layout
      return this.creditPromotions.find(promo => {
        return !this.excludePromotionIds.includes(promo.promotionId)
      })
    },
    hasStrikePrice () { // strike price uses promo filter, targetPromos does not
      return this.promoPrice.promoAmount !== this.fakeNormalPriceForBirthdayPromo // this.normalPrice.amount
    },
    fakeNormalPriceForBirthdayPromo () {
      if (this.birthdayPromo && this.selectedAboOptionId === '3036138') {
        return this.normalPrice.amount + 500
      }
      return this.normalPrice.amount
    }
  },
  methods: {
    getAriaPriceText () {
      this.$nextTick(() => {
        const prePriceText = (this.hasPricePromotion || this.showPriceStarts) && this.showPriceStarts ? 'ab ' : ''
        const priceText = this.priceWithoutEuro(this.promoPrice.promoAmount)
        const currencyText = this.getSlotText('txtPriceCurrency')

        const limitedPriceTexts = this.additionalPriceStairways.map((_, index) => {
          const ref = this.$refs['limitedPriceText' + index]
          const element = Array.isArray(ref) ? ref[0] : ref
          return element?.innerText?.trim() || ''
        }).filter(Boolean) // remove empty values and keep only valid non-empty texts

        this.ariaPriceText = this.processHTML(`${prePriceText}${priceText} ${currencyText} ${limitedPriceTexts.join(' ')}`)
      })
    },
    trackAndShowInfoModal () {
      this.showInfoModal = true
      const data = {
        information_action: 'reveal',
        information_name: 'price info modal',
        information_ui_type: 'overlay',
        information_trigger: 'icon'
      }
      this.trackData('information', data)
    },
    slotDataForStairway (stairway) {
      const price = { ...stairway }
      const prefix = this.showPriceStarts ? 'ab ' : ''
      price.promoAmount = prefix + this.priceWithoutEuro(stairway.promoAmount)
      return {
        promoPrice: price
      }
    }
  },
  watch: {
    promoPrice: {
      handler () {
        this.getAriaPriceText()
        this.ariaLivePriceText = 'polite'
      },
      deep: true
    }
  },
  props: {
    showTariffSelect: {
      type: Boolean,
      default: true
    },
    disableProviderSelect: {
      type: Boolean,
      default: false
    },
    quickSelectionBrand: {
      type: String,
      default: ''
    },
    showPriceStarts: {
      type: Boolean,
      default: false
    },
    normalPrice: {
      type: Object,
      default: null
    },
    promoPrice: {
      type: Object,
      default: null
    },
    additionalPriceStairways: {
      type: Array,
      default: () => []
    },
    targetTariff: {
      type: Object,
      default: null
    },
    targetPromotions: {
      type: Array,
      default: () => []
    },
    excludePromotionIds: {
      type: Array,
      default: () => []
    },
    process: {
      type: String,
      default: 'ordering'
    },
    ctaLink: {
      type: String,
      default: '/bestellung'
    }
  }
}
</script>
